import * as React from 'react';
import { useContext, useMemo } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation } from 'react-i18next';
import { useFareRulesQuery } from '@websky/graphql';
import { Mode, Locale } from '../../types';
import { ModeContext } from '../../context';
import { useTheme } from '../../theme';
import { useIsMobile } from '../../utils';
import { default as Button } from '../../Button';
import FareConditions from './FareConditions/FareConditions';
import ModalX from '../../BaseComponents/ModalX';
const FareConditionsModal = React.memo(({ orderId, flightId, language, segmentId, showTranslateTab, onLangChange, onClose, isOpen }) => {
    const { t } = useTranslation('FareConditions');
    const { FareConditions: css } = useTheme('FareConditions');
    const mode = useContext(ModeContext);
    const [selectedLang, selectLang] = React.useState(Locale.Russian);
    const [segment, setSegment] = React.useState(segmentId);
    const isMobile = useIsMobile();
    const modalSize = isMobile ? 'fullscreen' : 'xlarge';
    const { data, loading, error, refetch } = useFareRulesQuery({
        variables: { flightId: flightId !== null && flightId !== void 0 ? flightId : orderId, orderId, language },
        skip: mode !== Mode.Booking || !isOpen
    });
    const hasErrors = error || (!loading && !(data === null || data === void 0 ? void 0 : data.FareRules));
    const { fareRules, segmentTabs } = useMemo(() => {
        var _a, _b;
        if (loading || hasErrors || !((_b = (_a = data === null || data === void 0 ? void 0 : data.FareRules) === null || _a === void 0 ? void 0 : _a.rulesForSegments) === null || _b === void 0 ? void 0 : _b.length)) {
            return { fareRules: {}, segmentTabs: [] };
        }
        const fareRulesMap = {};
        const tabsSegmentsMap = new Map();
        data.FareRules.rulesForSegments.forEach(ruleForSegment => {
            ruleForSegment.segments.forEach(segment => {
                var _a, _b, _c, _d;
                const segmentId = segment.id;
                if (!fareRulesMap[segmentId]) {
                    fareRulesMap[segmentId] = [];
                }
                fareRulesMap[segmentId].push(ruleForSegment);
                if (!tabsSegmentsMap.has(segmentId)) {
                    const departureCityName = (_b = (_a = segment.departure.airport) === null || _a === void 0 ? void 0 : _a.city) === null || _b === void 0 ? void 0 : _b.name;
                    const arrivalCityName = (_d = (_c = segment.arrival.airport) === null || _c === void 0 ? void 0 : _c.city) === null || _d === void 0 ? void 0 : _d.name;
                    tabsSegmentsMap.set(segmentId, {
                        id: segmentId,
                        title: `${departureCityName} — ${arrivalCityName}`,
                        isDisabled: false
                    });
                }
            });
        });
        return {
            fareRules: fareRulesMap,
            segmentTabs: [...tabsSegmentsMap.values()]
        };
    }, [data, hasErrors, loading]);
    React.useEffect(() => {
        if (segmentTabs.length) {
            setSegment(segmentTabs[0].id);
        }
    }, [segmentTabs]);
    return (React.createElement(ModalX, { isOpen: isOpen, onClose: onClose, size: modalSize, scroll: "body", noPadding: true },
        loading && (React.createElement("div", { className: css.loaderWrp },
            React.createElement(CircularProgress, { classes: { colorPrimary: css.loader } }))),
        !loading && hasErrors && (React.createElement("div", { className: css.errorWrp },
            React.createElement("h6", { className: css.errorTitle }, t('Internal error')),
            error && React.createElement("p", { className: css.errorText }, error.message),
            React.createElement(Button, { size: "small", color: "primary", onClick: () => refetch({ flightId, language }) }, t('Try again')))),
        !loading && !hasErrors && (React.createElement(FareConditions, { fareRulesBySegmentsPassengersMap: fareRules, fareRules: data, segment: segment, setSegment: setSegment, onLangChange: onLangChange, selectedLang: selectedLang, selectLang: selectLang, tabsSegments: segmentTabs, showTranslateTab: showTranslateTab }))));
});
export default FareConditionsModal;
